import { useState } from 'react';
import type { Preset } from '../hooks/use-date-range.js';
import { Button } from './button.js';

interface DateRangePickerProps {
  since: Date;
  until: Date;
  preset: Preset;
  onPreset: (preset: Preset) => void;
  onCustomRange: (since: Date, until: Date) => void;
}

const PRESETS: { key: Preset; label: string }[] = [
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: 'this-month', label: 'This month' },
  { key: 'last-month', label: 'Last month' },
];

function toInputDate(d: Date): string {
  return d.toISOString().split('T')[0];
}

export function DateRangePicker({ since, until, preset, onPreset, onCustomRange }: DateRangePickerProps) {
  const [showCustom, setShowCustom] = useState(preset === 'custom');
  const [customFrom, setCustomFrom] = useState(toInputDate(since));
  const [customTo, setCustomTo] = useState(toInputDate(until));

  function handlePreset(p: Preset) {
    setShowCustom(false);
    onPreset(p);
  }

  function handleCustomToggle() {
    setShowCustom(true);
    setCustomFrom(toInputDate(since));
    setCustomTo(toInputDate(until));
  }

  function handleApply() {
    const s = new Date(customFrom);
    const u = new Date(customTo + 'T23:59:59.999');
    if (!isNaN(s.getTime()) && !isNaN(u.getTime()) && s < u) {
      onCustomRange(s, u);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {PRESETS.map(({ key, label }) => (
        <button
          key={key}
          type="button"
          onClick={() => handlePreset(key)}
          className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
            preset === key && !showCustom ? '' : 'border'
          }`}
          style={preset === key && !showCustom
            ? { backgroundColor: 'var(--color-brand)', color: 'white' }
            : { backgroundColor: 'var(--color-surface)', color: 'var(--color-text-2)', borderColor: 'var(--color-border)' }
          }
        >
          {label}
        </button>
      ))}
      <button
        type="button"
        onClick={handleCustomToggle}
        className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
          showCustom ? '' : 'border'
        }`}
        style={showCustom
          ? { backgroundColor: 'var(--color-brand)', color: 'white' }
          : { backgroundColor: 'var(--color-surface)', color: 'var(--color-text-2)', borderColor: 'var(--color-border)' }
        }
      >
        Custom
      </button>
      {showCustom && (
        <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center">
          <input
            type="date"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            className="form-input w-full rounded-lg border px-2 py-1.5 text-sm sm:w-auto"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }}
            aria-label="Start date"
          />
          <span className="hidden text-sm sm:inline" style={{ color: 'var(--color-text-3)' }}>to</span>
          <input
            type="date"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            className="form-input w-full rounded-lg border px-2 py-1.5 text-sm sm:w-auto"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }}
            aria-label="End date"
          />
          <Button onClick={handleApply} size="sm" className="w-full sm:w-auto">
            Apply
          </Button>
        </div>
      )}
    </div>
  );
}
